/**
 * Dependencies.
 */
import { debounce, orderBy, uniqBy } from 'lodash-es';
import { Component, Mixins } from 'vue-property-decorator';

import applicationConstant from '@/modules/main/constants/application.constant';
import ChooseChange from '@/modules/tracking/components/delivery/choose-change/choose-change.component.vue';
import ChooseDay from '@/modules/tracking/components/delivery/choose-day/choose-day.component.vue';
import ConfirmDay from '@/modules/tracking/components/delivery/confirm-day/confirm-day.component.vue';
import ProcessingInformation from '@/modules/tracking/components/delivery/processing/processing-info.component.vue';
import PickUpInformation from '@/modules/tracking/components/delivery/service-point/pick-up-info.component.vue';
import DriverInformation from '@/modules/tracking/components/driver/driver-information.component.vue';
import { IHistory } from '@/modules/tracking/components/history/history.component';
import History from '@/modules/tracking/components/history/history.component.vue';
import MapTrackingVehicle from '@/modules/tracking/components/map/tracking-vehicle.component.vue';
import StatusCard from '@/modules/tracking/components/status/card/status-card.component.vue';
import { TRACKING_STATUS } from '@/modules/tracking/constants';
import TimelineMixin from '@/modules/tracking/mixins/timeline.mixin';
import TrackingMixin from '@/modules/tracking/mixins/tracking.mixin';
import { trackingStore } from '@/modules/tracking/store';
import Error from '@/ui/components/error.component.vue';
import NotifyClientRequest from '@/ui/components/notify/client-request.component.vue';
import Timeline from '@/ui/components/timeline/timeline.component.vue';

/**
 * Component.
 */
@Component({
  components: {
    ChooseChange,
    ChooseDay,
    ConfirmDay,
    DriverInformation,
    Error,
    History,
    MapTrackingVehicle,
    NotifyClientRequest,
    PickUpInformation,
    ProcessingInformation,
    StatusCard,
    Timeline
  },
  name: 'tracking-scene'
})
export default class extends Mixins(TrackingMixin, TimelineMixin) {
  /**
   * Property `firstTwoBlockElementsHeight`.
   */
  firstTwoBlockElementsHeight = 108;

  /**
   * Property `sheetInitialHeight`.
   */
  sheetInitialHeight = 0;

  /**
   * Property `statusElementHeight`.
   */
  statusElementHeight = 0;

  /**
   * Property `sheetExpandedHeight`.
   */
  sheetExpandedHeight = 0;

  /**
   * Property `dayChanged`.
   */
  dayChanged = null;

  /**
   * Property `isBottomSheetVisible`.
   */
  isBottomSheetVisible = true;

  /**
   * Property `isBottomSheetExpanded`.
   */
  isBottomSheetExpanded = false;

  /**
   * Property `windowHeight`.
   */
  windowHeight = null;

  /**
   * Property `windowInnerHeight`.
   */
  windowInnerHeight = 0;

  /**
   * Property `canShowDriverInfoAndActions`.
   */
  canShowDriverInfoAndActions = true;

  /**
   * Getter `betweenFromDate`.
   */
  get betweenFromDate() {
    return trackingStore.betweenFromDate;
  }

  /**
   * Getter `betweenToDate`.
   */
  get betweenToDate() {
    return trackingStore.betweenToDate;
  }

  /**
   * Getter `deliveryAddress`.
   */
  get deliveryAddress() {
    const { address = null } = this.order || {};

    return address;
  }

  /**
   * Getter `deliveryCoordinates`.
   */
  get deliveryCoordinates() {
    const { address } = this.order || {};
    const { latitude = null, longitude = null } = address || {};

    return { latitude, longitude };
  }

  /**
   * Getter `vehicleCoordinates`.
   */
  get vehicleCoordinates() {
    return trackingStore.vehicleCoordinates;
  }

  /**
   * Getter `distributionCenter`.
   */
  get distributionCenter() {
    return trackingStore.distributionCenter?.name;
  }

  /**
   * Getter driver.
   */
  get driver() {
    return trackingStore.driver;
  }

  /**
   * Getter `servicePoint`.
   */
  get servicePoint() {
    const { address } = this.order || {};
    const { servicePoint } = address || {};
    const { name = null } = servicePoint || {};

    return name;
  }

  /**
   * Getter vehicle.
   */
  get vehicle() {
    return trackingStore.vehicle;
  }

  /**
   * Getter is status processing.
   */
  get isStatusProcessing() {
    const { PROCESSING } = this.trackingStatus;
    const { status: { code = null } = {} } = this.currentTracking || {};

    return code === PROCESSING;
  }

  /**
   * Getter is status in routing.
   */
  get isStatusInRouting() {
    const { IN_ROUTING } = this.trackingStatus;
    const { status: { code = null } = {} } = this.currentTracking || {};

    return code === IN_ROUTING;
  }

  /**
   * Getter is status successuful.
   */
  get isStatusSuccessful() {
    const { COLLECTED, DELIVERED } = this.trackingStatus;
    const { status: { code = null } = {} } = this.currentTracking || {};
    const isVisitedSuccessfully = code === DELIVERED || code === COLLECTED;

    return isVisitedSuccessfully;
  }

  /**
   * Getter is status in service point.
   */
  get isStatusInServicePoint() {
    const { IN_SERVICE_POINT } = this.trackingStatus;
    const { status: { code = null } = {} } = this.currentTracking || {};

    return code === IN_SERVICE_POINT;
  }

  /**
   * Getter is status in interruped route.
   */
  get isStatusInInterruptedRoute() {
    const { IN_INTERRUPTED_ROUTE } = this.trackingStatus;
    const { status: { code = null } = {} } = this.currentTracking || {};

    return code === IN_INTERRUPTED_ROUTE;
  }

  /**
   * Getter on the way to service point.
   */
  get onTheWayToServicePoint() {
    const { address = null } = this.order || {};
    const { servicePoint = false } = address || {};
    const onTheWayToServicePoint = !!servicePoint;

    return onTheWayToServicePoint;
  }

  /**
   * Getter is status unsuccess.
   */
  get isStatusUnsuccess() {
    const {
      CANCELED,
      IN_TREATMENT,
      NOT_COLLECTED,
      NOT_DELIVERED,
      RETURNED
    } = this.trackingStatus;
    const { status: { code = null } = {} } = this.currentTracking || {};

    return (
      code === NOT_COLLECTED ||
      code === NOT_DELIVERED ||
      code === IN_TREATMENT ||
      code === RETURNED ||
      code === CANCELED
    );
  }

  /**
   * Getter is status returned.
   */
  get isStatusReturned() {
    const { RETURNED } = this.trackingStatus;
    const { status: { code = null } = {} } = this.currentTracking || {};

    return code === RETURNED;
  }

  /**
   * Getter is status canceled.
   */
  get isStatusCanceled() {
    const { CANCELED } = this.trackingStatus;
    const { status: { code = null } = {} } = this.currentTracking || {};

    return code === CANCELED;
  }

  /**
   * Getter is status collected.
   */
  get isStatusCollected() {
    const { COLLECTED } = this.trackingStatus;
    const { status: { code = null } = {} } = this.currentTracking || {};

    return code === COLLECTED;
  }

  /**
   * Getter can show pick up information.
   */
  get canShowPickUpInformation() {
    const {
      isDeliveredToServicePoint,
      isStatusInServicePoint,
      isToCollectInServicePoint
    } = this || {};

    return (
      isStatusInServicePoint ||
      isDeliveredToServicePoint ||
      isToCollectInServicePoint
    );
  }

  /**
   * Getter can show driver information.
   */
  get canShowDriverInformation() {
    const {
      isDeliveredToServicePoint,
      isStatusInRouting,
      isStatusSuccessful,
      isStatusUnsuccess,
      isToCollectInServicePoint
    } = this || {};

    return (
      !isDeliveredToServicePoint &&
      (isStatusInRouting ||
        isStatusSuccessful ||
        isStatusUnsuccess ||
        this.isStatusInInterruptedRoute) &&
      !isToCollectInServicePoint
    );
  }

  /**
   * Getter can show more information.
   */
  get canShowMoreInformation() {
    const {
      canShowDriverInfoAndActions,
      isBottomSheetExpanded,
      isSmallScreenHeight
    } = this || {};

    return (
      canShowDriverInfoAndActions &&
      (!isSmallScreenHeight || isBottomSheetExpanded)
    );
  }

  /**
   * Getter is delivered to service point.
   */
  get isDeliveredToServicePoint() {
    const { isStatusSuccessful } = this || {};
    const { address = null } = this.order || {};
    const { servicePoint = false } = address || {};
    const isForServicePoint = !!servicePoint;

    return isForServicePoint && isStatusSuccessful;
  }

  /**
   * Getter `canShowContactOrMessageAction`.
   */
  get canShowContactOrMessageAction() {
    const trackingHistory = trackingStore.tracking || [];
    const hasBeenOnRoute =
      trackingHistory.findIndex(
        tracking => tracking?.status?.code === TRACKING_STATUS.IN_ROUTING
      ) !== -1;

    return !(this.isStatusProcessing && !hasBeenOnRoute);
  }

  /**
   * Getter map height.
   */
  get mapHeight() {
    return `calc(${this.windowInnerHeight}px - ${this.firstTwoBlockElementsHeight}px - ${this.statusElementHeight}px - ${this.sheetInitialHeight}px + 8px)`;
  }

  /**
   * Getter can show in sheet tracking history.
   */
  get canShowTrackingHistory() {
    return this.isBottomSheetExpanded && !this.error;
  }

  /**
   * Getter marker type.
   */
  get markerType() {
    let marker = 'inRouting';

    if (this.isStatusProcessing) {
      marker = 'processing';
    }

    if (this.isStatusInServicePoint) {
      marker = 'inServicePoint';
    }

    if (this.onTheWayToServicePoint) {
      marker = 'onTheWayToServicePoint';
    }

    if (this.isStatusSuccessful) {
      marker = 'delivered';
    }

    if (this.isStatusInServicePoint && this.isToCollectInServicePoint) {
      marker = 'delivered';
    }

    if (this.isStatusUnsuccess) {
      marker = 'notDelivered';
    }

    return marker;
  }

  /**
   * Getter `vehicleType`.
   */
  get vehicleType() {
    let result: 'van' | 'moto' = null;

    const { vehicleType = null } = this.vehicle || {};
    const { code = null } = vehicleType || {};

    switch (code) {
      case 'MTC':
        result = 'moto';
        break;
      case 'VANNC':
        result = 'van';
        break;
      case 'VANNE':
        result = 'van';
        break;
      default:
        result = 'van';
        break;
    }

    return result;
  }

  /**
   * Getter already submited.
   */
  get alreadySubmited() {
    const { alreadySubmited = false } = trackingStore.deliverySurvey || {};

    return alreadySubmited;
  }

  /**
   * Getter `history`.
   */
  get history() {
    const trackingHistory = trackingStore.tracking || [];
    const requestsHistory = trackingStore.requests || [];

    const history: IHistory[] = [];

    trackingHistory.forEach(item => {
      const {
        hideHistory = false,
        message = null,
        status = null,
        statusDate = null
      } = item || {};

      history.push({
        hideHistory,
        historyType: 'tracking',
        status,
        statusDate,
        tracking: {
          message
        }
      });
    });

    requestsHistory.map(item => {
      const {
        id = null,
        isInternal = null,
        request = null,
        status = null,
        statusDate = null,
        type = null
      } = item || {};
      const { from = null, to = null } = request || {};

      history.push({
        hideHistory: false,
        historyType: 'request',
        request: {
          from,
          id,
          isInternal,
          to,
          type
        },
        status,
        statusDate
      });
    });

    return orderBy(
      history,
      [item => (item?.statusDate ? new Date(item.statusDate).getTime() : 0)],
      ['desc']
    );
  }

  /**
   * Getter small screen height.
   */
  get isSmallScreenHeight() {
    return this.windowHeight <= 568;
  }

  /**
   * Getter sheet height.
   */
  get sheetHeight() {
    return this.isBottomSheetExpanded && this.sheetExpandedHeight !== 0
      ? `${this.sheetExpandedHeight}px`
      : 'auto';
  }

  /**
   * Getter support url.
   */
  get supportUrl() {
    const { address } = this.order || {};
    const { countryCode = '' } = address || {};
    const { lang: { currentLanguage: { code: languageCode = '' } = {} } = {} } =
      this.$kit || {};

    let { supportUrl = '' } = applicationConstant || {};

    supportUrl = supportUrl
      .replace('{store}', countryCode.toLocaleLowerCase())
      .replace('{language}', languageCode.toLocaleLowerCase());

    return supportUrl;
  }

  /**
   * Getter window resize debounced.
   */
  get windowResizeDebounced() {
    return debounce(this.handleWindowResize, 100);
  }

  /**
   * Getter total of unread messages.
   */
  get totalUnreadMessages() {
    let messages = [...trackingStore.chatMessages];

    const deletedMessages = uniqBy(
      messages.filter(({ source }) => source === 'DELETED'),
      'id'
    );
    const deleted = deletedMessages.map(({ id }) => id);

    messages = uniqBy(
      messages.filter(
        ({ id, source }) =>
          source !== 'DELETED' && source !== 'READED' && !deleted.includes(id)
      ),
      'id'
    );

    return messages.length;
  }

  /**
   * Getter total unresolved contact.
   */
  get totalUnresolvedContact() {
    return 1;
  }

  /**
   * Lifecycle mounted hook.
   */
  async mounted() {
    this.$on('trackingDataUpdated', () => {
      this.handleWindowResize();
    });

    await this.handleInitializeMixin();

    this.handleWindowResize();

    window.addEventListener('resize', this.windowResizeDebounced);
  }

  /**
   * Lifecycle destroyed hook.
   */
  destroyed() {
    window.removeEventListener('resize', this.windowResizeDebounced);
  }

  /**
   * Handle bottom sheet click.
   */
  handleBottomSheetClick() {
    this.isBottomSheetExpanded = !this.canShowDriverInfoAndActions
      ? false
      : !this.isBottomSheetExpanded;

    if (!this.canShowDriverInfoAndActions) {
      this.handleWindowResize();
    }

    this.canShowDriverInfoAndActions = true;
  }

  /**
   * Handle window resize.
   */
  handleWindowResize() {
    this.windowHeight = document.documentElement.clientHeight;
    this.windowInnerHeight = window.innerHeight;
    this.firstTwoBlockElementsHeight = this.windowHeight <= 568 ? 76 : 108;
    this.isBottomSheetExpanded = false;

    this.$nextTick().then(() => {
      if (!this.isBottomSheetExpanded) {
        this.sheetExpandedHeight =
          (window?.innerHeight || 0) - this.firstTwoBlockElementsHeight;

        this.sheetInitialHeight = (this.$refs.sheet as Vue)?.$el?.clientHeight;
        this.statusElementHeight = (this.$refs
          .status as Vue)?.$el?.clientHeight;
      }
    });
  }

  /**
   * Handle cancel delivery request.
   */
  async handleCancelDeliveryRequest() {
    this.$router.push({
      name: 'RequestCancel',
      params: {
        key: this.key,
        type: 'request'
      }
    });
  }

  /**
   * Handle sheet swipe.
   */
  handleSheetSwipe(direction) {
    switch (direction) {
      case 'up':
        if (!this.canShowDriverInfoAndActions) {
          this.canShowDriverInfoAndActions = true;
          this.handleWindowResize();
        } else {
          this.isBottomSheetExpanded = true;
        }
        break;

      case 'down':
        if (!this.isBottomSheetExpanded) {
          this.canShowDriverInfoAndActions = false;
          this.handleWindowResize();
        }

        this.isBottomSheetExpanded = false;
        break;
    }
  }

  /**
   * Handle request contact.
   */
  handleRequestContact() {
    this.$router.push({
      name: 'RequestContact',
      params: {
        key: this.key
      }
    });
  }

  /**
   * Handle view request message.
   */
  handleViewRequestMessage() {
    this.$router.push({
      name: 'RequestMessageChat',
      params: {
        key: this.key
      }
    });
  }

  /**
   * Handle change address.
   */
  handleChangeDelivery() {
    trackingStore.setChangeDeliveryAddress({ address: null });
    trackingStore.setChangeDeliveryDay({ day: null });

    this.$router.push({
      name: 'RequestChooseChange',
      params: {
        key: this.key
      }
    });
  }

  /**
   * Handle view pick up code.
   */
  handleViewPickUpCode() {
    this.$router.push({
      name: 'PickUpCode',
      params: { key: this.key }
    });
  }

  /**
   * Handle go to evaluation.
   */
  handleGoToEvaluation() {
    this.$router.push({
      name: 'TrackingSurvey',
      params: {
        key: this.key
      }
    });
  }
}
